import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  PersonOutline
} from '@mui/icons-material';
import BrandLogo from './BrandLogo';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onLogin({
        username: username.trim(),
        password
      });
    } catch (loginError) {
      setError(loginError.message || 'Unable to sign in right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0B0D0F',
      p: 2
    }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{
          p: { xs: 3, sm: 4.5, md: 5 },
          borderRadius: 2,
          background: '#15181C',
          border: '1px solid #2D333A',
          borderTop: '3px solid #E13B36',
          textAlign: 'left'
        }}>
          <BrandLogo height={52} sx={{ mb: 4 }} />
          
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#F4F6F8', mb: 1, letterSpacing: '-0.035em' }}>
            Admin sign in
          </Typography>
          <Typography variant="body2" sx={{ color: '#9AA1A9', mb: 4, lineHeight: 1.7 }}>
            Use your AzTires credentials to open inventory, sales, and reporting tools.
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              sx={{ mb: 3, '& .MuiInputBase-input': { fontSize: '16px' } }}
              autoComplete="username"
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                      <PersonOutline sx={{ color: '#7F8790' }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '16px' } }}
              autoComplete="current-password"
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#7F8790' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ minHeight: 44, minWidth: 44, color: '#9E9E9E' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ minHeight: 24, mb: 3, mt: 1, textAlign: 'left' }}>
              {error && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {error}
                </Alert>
              )}
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                background: '#E13B36',
                boxShadow: 'none',
                '&:hover': {
                  background: '#F05A54',
                  boxShadow: 'none',
                },
                transition: 'background-color 0.18s'
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>
          
          <Button
            href="/"
            sx={{ mt: 3, px: 0, color: '#9AA1A9', fontWeight: 600, minHeight: 44, '&:hover': { color: '#E13B36', bgcolor: 'transparent' } }}
          >
            Return to Store
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
